export type UxSignalPayload = Record<string, unknown>;

export type UxSignal = {
  event: string;
  status?: 'ok' | 'error' | 'info';
  durationMs?: number;
  at: string;
  payload?: UxSignalPayload;
};

declare global {
  interface Window {
    __platformUxSignals?: UxSignal[];
  }
}

const MAX_SIGNALS = 300;

function storeSignal(signal: UxSignal): void {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(window.__platformUxSignals)) {
    window.__platformUxSignals = [];
  }
  window.__platformUxSignals.push(signal);
  if (window.__platformUxSignals.length > MAX_SIGNALS) {
    window.__platformUxSignals = window.__platformUxSignals.slice(-MAX_SIGNALS);
  }
}

export function trackUxSignal(event: string, payload?: UxSignalPayload): void {
  storeSignal({
    event,
    status: 'info',
    at: new Date().toISOString(),
    payload,
  });
}

export async function measureAsync<T>(
  event: string,
  task: () => Promise<T>,
  payload?: UxSignalPayload,
): Promise<T> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  try {
    const output = await task();
    const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    storeSignal({
      event,
      status: 'ok',
      durationMs: Math.round(endedAt - startedAt),
      at: new Date().toISOString(),
      payload,
    });
    return output;
  } catch (error) {
    const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    storeSignal({
      event,
      status: 'error',
      durationMs: Math.round(endedAt - startedAt),
      at: new Date().toISOString(),
      payload: {
        ...(payload || {}),
        error: error instanceof Error ? error.message : String(error),
      },
    });
    throw error;
  }
}
